import { useCallback, useEffect, useState } from 'react';
import type { MediaDevice, TrackKind } from '@shareview/tele-engine-core';
import { useTeleSession, useTeleSnapshot } from '@shareview/tele-engine-react';
import { formatIce, formatRow } from './stats';
import { ParticipantTile } from './tile';
import { useCallStats } from './use-call-stats';

/** In-call screen: controls, device pickers, participant grid keyed by participant id, stats. */
export function CallView({ onLeave }: { onLeave: () => void }) {
  const session = useTeleSession();
  const local = useTeleSnapshot((s) => s.localParticipant);
  const remotes = useTeleSnapshot((s) => s.remoteParticipants);
  const audioMuted = useTeleSnapshot((s) => s.audioMuted);
  const videoMuted = useTeleSnapshot((s) => s.videoMuted);
  const audioBlocked = useTeleSnapshot((s) => s.audioPlaybackBlocked);
  const state = useTeleSnapshot((s) => s.state);
  const [showStats, setShowStats] = useState(true);
  const [actionError, setActionError] = useState<string>();
  const rows = useCallStats(showStats);

  const hasCamera = local?.tracks.some((t) => t.kind === 'video') ?? false;
  const run = useCallback((action: () => Promise<void>) => {
    setActionError(undefined);
    action().catch((e: unknown) => setActionError(String(e)));
  }, []);

  // Guests join without a camera: the first "Camera on" captures and publishes it.
  const toggleCamera = () =>
    run(() => (hasCamera ? session.mute('video', !videoMuted) : session.publish({ video: true })));

  return (
    <>
      <div className="controls">
        <button data-testid="toggle-mic" onClick={() => run(() => session.mute('audio', !audioMuted))}>
          {audioMuted ? 'Unmute mic' : 'Mute mic'}
        </button>
        <button data-testid="toggle-cam" onClick={toggleCamera}>
          {hasCamera && !videoMuted ? 'Camera off' : 'Camera on'}
        </button>
        <button data-testid="leave" onClick={onLeave}>
          Leave
        </button>
        <button onClick={() => setShowStats((s) => !s)}>{showStats ? 'Hide stats' : 'Show stats'}</button>
        {audioBlocked && (
          <button className="primary" data-testid="start-audio" onClick={() => run(() => session.startAudio())}>
            Start audio (blocked by autoplay)
          </button>
        )}
        <DevicePickers />
        {state === 'reconnecting' && <span className="warn">reconnecting...</span>}
      </div>
      {actionError && <pre className="error">{actionError}</pre>}
      <div className="grid">
        {local && <ParticipantTile key={local.id} participant={local} />}
        {remotes.map((p) => (
          <ParticipantTile key={p.id} participant={p} />
        ))}
      </div>
      {showStats && (
        <pre className="stats" data-testid="stats">
          {`state=${state} participants=${remotes.length + 1} me=${local?.id ?? '-'}\n`}
          {rows ? [...rows.map(formatRow), formatIce(rows)].join('\n') || 'no tracks' : 'collecting...'}
        </pre>
      )}
    </>
  );
}

/** Camera and microphone pickers; `switchDevice` swaps the published track and keeps mute state. */
function DevicePickers() {
  const session = useTeleSession();
  const selected = useTeleSnapshot((s) => s.selectedDevices);
  const local = useTeleSnapshot((s) => s.localParticipant);
  const [devices, setDevices] = useState<MediaDevice[]>([]);

  // Labels are only filled in once capture permission is granted, so re-list when local tracks change.
  const trackKey = local?.tracks.map((t) => t.id).join(',') ?? '';
  useEffect(() => {
    let cancelled = false;
    session
      .listDevices()
      .then((list) => !cancelled && setDevices(list))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [session, trackKey]);

  const current = (kind: TrackKind) => {
    const fromSelection = kind === 'audio' ? selected.audioinput : selected.videoinput;
    const track = local?.tracks.find((t) => t.kind === kind)?.mediaStreamTrack;
    return fromSelection ?? track?.getSettings?.().deviceId ?? '';
  };

  const picker = (kind: TrackKind, deviceKind: MediaDevice['kind'], label: string) => {
    const options = devices.filter((d) => d.kind === deviceKind);
    if (options.length === 0) return null;
    return (
      <label className="picker">
        {label}{' '}
        <select
          data-testid={`device-${kind}`}
          value={current(kind)}
          onChange={(e) => void session.switchDevice(kind, e.target.value).catch((err) => console.warn(err))}
        >
          {!current(kind) && <option value="">(default)</option>}
          {options.map((d, i) => (
            <option key={d.deviceId || i} value={d.deviceId}>
              {d.label || `${label} ${i + 1}`}
            </option>
          ))}
        </select>
      </label>
    );
  };

  return (
    <>
      {picker('audio', 'audioinput', 'Mic')}
      {picker('video', 'videoinput', 'Camera')}
    </>
  );
}
